/**
 * MYA colour palette — the ONLY colour input to the live renderer.
 *
 * Five editable roles map 1:1 onto the roles the engine already drew with its
 * former hard-coded gold constants:
 *
 *   base     — strand core / body tone            (was GOLD)
 *   node     — bright node cores + strand hot spots (was HOT)
 *   heat     — warm mid / under-glow pass          (was AMBER)
 *   glow     — atmospheric glow + outer falloff    (was EMBER)
 *   particle — spark / mote cores                  (was HOT)
 *
 * Colour is purely a paint-time concern: the palette never touches the
 * simulation, geometry, spawn counts or motion. `ORIGINAL` reproduces the
 * previous output byte-for-byte.
 */

export type MyaPaletteRole = "base" | "node" | "heat" | "glow" | "particle";

export const MYA_PALETTE_ROLES: readonly MyaPaletteRole[] = [
  "base",
  "node",
  "heat",
  "glow",
  "particle",
];

export const MYA_PALETTE_ROLE_LABELS: Record<MyaPaletteRole, string> = {
  base: "Base",
  node: "Node",
  heat: "Heat",
  glow: "Glow",
  particle: "Particle",
};

/** Hex colours (`#rrggbb`) per role. */
export type MyaPalette = Record<MyaPaletteRole, string>;

export type MyaPalettePresetId =
  | "original"
  | "sith-red"
  | "dark-energy"
  | "electric-cyan"
  | "ultraviolet"
  | "toxic-emerald"
  | "solar-fire"
  | "gold"
  | "custom";

export type MyaPalettePreset = {
  id: MyaPalettePresetId;
  label: string;
  palette: MyaPalette;
};

/** Exact previous constants (HOT / GOLD / AMBER / EMBER) — rollback-identical. */
export const MYA_ORIGINAL_PALETTE: MyaPalette = {
  base: "#ffce74", // 255, 206, 116
  node: "#fff9e8", // 255, 249, 232
  heat: "#f6a63e", // 246, 166, 62
  glow: "#a85612", // 168, 86, 18
  particle: "#fff9e8",
};

export const MYA_PALETTE_PRESETS: readonly MyaPalettePreset[] = [
  { id: "original", label: "Original", palette: MYA_ORIGINAL_PALETTE },
  {
    id: "sith-red",
    label: "Sith Red",
    palette: { base: "#ff2a2a", node: "#ffe6e6", heat: "#d40f0f", glow: "#5a0404", particle: "#ffb3b3" },
  },
  {
    id: "dark-energy",
    label: "Dark Energy",
    palette: { base: "#8a5cff", node: "#f2ecff", heat: "#4b1fb8", glow: "#160a3a", particle: "#c9b6ff" },
  },
  {
    id: "electric-cyan",
    label: "Electric Cyan",
    palette: { base: "#35e6ff", node: "#eafeff", heat: "#0aa8d6", glow: "#043a52", particle: "#b8f7ff" },
  },
  {
    id: "ultraviolet",
    label: "Ultraviolet",
    palette: { base: "#c04dff", node: "#fbe9ff", heat: "#7a12d8", glow: "#2a0650", particle: "#e6b3ff" },
  },
  {
    id: "toxic-emerald",
    label: "Toxic Emerald",
    palette: { base: "#3dff7a", node: "#ecffef", heat: "#0fbf4a", glow: "#053f1b", particle: "#b8ffcf" },
  },
  {
    id: "solar-fire",
    label: "Solar / Fire",
    palette: { base: "#ff9a1f", node: "#fff4d6", heat: "#ff4d00", glow: "#7a1400", particle: "#ffd28a" },
  },
  {
    id: "gold",
    label: "Gold",
    palette: { base: "#ffd54a", node: "#fffbe0", heat: "#e0a100", glow: "#7a5200", particle: "#fff0a8" },
  },
  // "custom" holds whatever the editable controls produce; it starts from Original.
  { id: "custom", label: "Custom", palette: MYA_ORIGINAL_PALETTE },
];

export function getPalettePreset(id: MyaPalettePresetId): MyaPalettePreset {
  return MYA_PALETTE_PRESETS.find((p) => p.id === id) ?? MYA_PALETTE_PRESETS[0]!;
}

const HEX_RE = /^#?([0-9a-f]{6})$/i;
const SHORT_HEX_RE = /^#?([0-9a-f]{3})$/i;

/** `#rrggbb` (or `#rgb`) → `"r, g, b"` for the engine's `rgba()` helper. */
export function hexToRgbTriplet(hex: string, fallback = "255, 206, 116"): string {
  const s = (hex ?? "").trim();
  const m = s.match(HEX_RE);
  let h: string | undefined = m?.[1];
  if (!h) {
    const sm = s.match(SHORT_HEX_RE);
    if (sm) h = sm[1]!.split("").map((c) => c + c).join("");
  }
  if (!h) return fallback;
  const n = parseInt(h, 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

/** Engine-side resolved palette: `"r, g, b"` triplets per role. */
export type MyaResolvedPalette = Record<MyaPaletteRole, string>;

export function resolvePalette(palette?: Partial<MyaPalette> | null): MyaResolvedPalette {
  const p = { ...MYA_ORIGINAL_PALETTE, ...(palette ?? {}) };
  const out = {} as MyaResolvedPalette;
  for (const role of MYA_PALETTE_ROLES) {
    out[role] = hexToRgbTriplet(p[role], hexToRgbTriplet(MYA_ORIGINAL_PALETTE[role]));
  }
  return out;
}

/** Which preset (if any) a palette exactly matches; otherwise `custom`. */
export function identifyPalette(palette: MyaPalette): MyaPalettePresetId {
  const norm = (p: MyaPalette) => MYA_PALETTE_ROLES.map((r) => p[r].toLowerCase()).join("|");
  const key = norm(palette);
  for (const preset of MYA_PALETTE_PRESETS) {
    if (preset.id === "custom") continue;
    if (norm(preset.palette) === key) return preset.id;
  }
  return "custom";
}
